
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, Star, AlertTriangle, ShieldAlert } from 'lucide-react';
import { saveRedirectPath } from '@/utils/auth-redirect';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isLicensedPractitioner } from '@/utils/practitioner-mode';
import { useLocalCurrency } from '@/hooks/useLocalCurrency';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import { cn } from '@/lib/utils';
import { createSafeHTML } from '@/utils/sanitization';
import { useFastNavigation } from '@/hooks/useFastNavigation';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  service_name_en?: string | null;
  service_name_zh?: string | null;
  duration_minutes: number;
  price: number;
  currency: string;
  description?: string;
  description_en?: string | null;
  description_zh?: string | null;
  is_free_session?: boolean;
  formats_available?: string[];
  therapy_tags?: ServiceTherapyTag[];
  service_track?: 'clinical' | 'wellness';
  adults_only?: boolean;
  parental_consent_required?: boolean;
}

interface PractitionerServicesProps {
  services: Service[];
  selectedService: string | null;
  onServiceSelect: (serviceId: string | null) => void;
  onBookService: (serviceId: string) => void;
  practitionerId?: string;
  practitionerName?: string;
  professionalTitle?: string;
  avatarUrl?: string;
  practitionerSlug?: string;
  practitionerTimezone?: string;
  practiceMode?: 'licensed' | 'wellness' | null;
  userState?: string | null;
  userCountry?: string | null;
  practitionerJurisdiction?: string | null;
}

// Collapsible description sub-component
const CollapsibleDescription: React.FC<{ html: string }> = ({ html }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [isClamped, setIsClamped] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight + 2);
  }, [html]);

  const isHtml = /<[a-z][\s\S]*>/i.test(html);

  return (
    <div className="mb-3">
      {isHtml ? (
        <div
          ref={ref}
          className={cn('prose prose-sm max-w-none text-sm text-gray-600', !expanded && 'line-clamp-3')}
          dangerouslySetInnerHTML={createSafeHTML(html)}
        />
      ) : (
        <p
          ref={ref as React.RefObject<HTMLParagraphElement>}
          className={cn('text-sm text-gray-600 whitespace-pre-wrap', !expanded && 'line-clamp-3')}
        >
          {html}
        </p>
      )}
      {(isClamped || expanded) && (
        <div className="text-right mt-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="text-xs text-primary hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        </div>
      )}
    </div>
  );
};

const PractitionerServices: React.FC<PractitionerServicesProps> = ({
  services,
  selectedService,
  onServiceSelect,
  onBookService,
  practitionerId,
  practitionerName,
  professionalTitle,
  avatarUrl,
  practitionerSlug,
  practitionerTimezone,
  practiceMode,
  userState,
  userCountry,
  practitionerJurisdiction
}) => {
  const navigate = useNavigate();
  const { user, activeRole } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('practitioners');
  const [showRoleAlert, setShowRoleAlert] = React.useState(false);
  const [showClinicalStateWarning, setShowClinicalStateWarning] = React.useState(false);
  const [pendingClinicalService, setPendingClinicalService] = React.useState<Service | null>(null);
  const [clinicalWarningAccepted, setClinicalWarningAccepted] = React.useState(false);
  const [pendingBookingServiceId, setPendingBookingServiceId] = React.useState<string | null>(null);
  const isLicensedMode = isLicensedPractitioner(practiceMode);
  const { userCurrency, formatLocal, isLoading: ratesLoading } = useLocalCurrency();
  const { fastNavigate, prefetch } = useFastNavigation();

  const proceedToBooking = (service: Service) => {
    const returnToPath = `/book-an-appointment/${practitionerSlug || practitionerId}`;
    const isServiceClinical = service.service_track === 'clinical';
    const tempBookingInfo = {
      practitionerId: practitionerId,
      practitionerName: practitionerName || 'Unknown Practitioner',
      serviceId: service.id,
      serviceName: service.service_name,
      price: isServiceClinical ? 0 : service.price,
      currency: service.currency,
      duration: service.duration_minutes,
      professionalTitle: professionalTitle,
      avatarUrl: avatarUrl,
      returnToPath,
      practitionerTimezone: practitionerTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      bookingMode: isServiceClinical ? 'request_only' : 'paid',
      adults_only: service.adults_only || false,
      parental_consent_required: service.parental_consent_required || false,
      formatsAvailable: service.formats_available || ['virtual'],
    };
    console.log("Storing temporary booking info with timezone:", tempBookingInfo);
    sessionStorage.setItem('tempBookingInfo', JSON.stringify(tempBookingInfo));
    sessionStorage.removeItem('bookingInfo');
    fastNavigate('/booking-datetime');
  };

  const handleBookNow = (service: Service) => {
    setPendingBookingServiceId(service.id);
    console.log("Current active role:", activeRole);

    if (!user) {
      toast({
        title: "Login Required",
        description: t('loginRequired'),
        variant: "default"
      });
      saveRedirectPath();
      fastNavigate('/client-auth');
      return;
    }

    if (activeRole === 'practitioner' || activeRole === 'admin') {
      setShowRoleAlert(true);
      setPendingBookingServiceId(null);
      return;
    }

    if (activeRole === 'client') {
      if (!practitionerId || !practitionerName) {
        toast({
          variant: "destructive",
          title: "Missing practitioner information",
          description: t('selectPractitionerAgain')
        });
        setPendingBookingServiceId(null);
        return;
      }

      if (service.service_track === 'clinical' && userState && practitionerJurisdiction) {
        const jurisdictionLower = practitionerJurisdiction.toLowerCase();
        const userStateLower = userState.toLowerCase();
        if (!jurisdictionLower.includes(userStateLower)) {
          setPendingClinicalService(service);
          setClinicalWarningAccepted(false);
          setShowClinicalStateWarning(true);
          setPendingBookingServiceId(null);
          return;
        }
      }

      proceedToBooking(service);
    }
  };

  const handleClinicalWarningConfirm = () => {
    if (pendingClinicalService) {
      proceedToBooking(pendingClinicalService);
    }
    setShowClinicalStateWarning(false);
    setPendingClinicalService(null);
    setClinicalWarningAccepted(false);
  };

  const handleGoToClientLogin = () => {
    navigate('/client-auth');
    setShowRoleAlert(false);
  };

  if (!services || services.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <p className="text-gray-600">{t('noServicesAvailable')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <div className="space-y-3">
          {services.map((service) => {
            const displayDesc = getBilingualValue(i18n.language, service.description_en, service.description_zh, service.description);

            return (
              <Card 
                key={service.id} 
                className={`cursor-pointer transition-colors ${
                  selectedService === service.id 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'hover:border-amber-200'
                }`}
                onClick={() => onServiceSelect(service.id)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-lg">{getBilingualValue(i18n.language, service.service_name_en, service.service_name_zh, service.service_name)}</h4>
                        {service.service_track === 'clinical' && (
                          <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs">
                            Clinical
                          </Badge>
                        )}
                        {selectedService === service.id && (
                          <CheckCircle className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{service.duration_minutes} {t('minutes')}</span>
                        </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-lg text-gray-900">
                              {typeof service.price === 'number' ? formatLocal(service.price, service.currency || 'USD') : '-'}
                            </span>
                            {(service.service_track === 'clinical' || (isLicensedMode && !service.service_track)) && (
                              <span className="text-xs text-amber-600 font-medium">{t('referencePrice')}</span>
                            )}
                            {typeof service.price === 'number' && (service.currency || 'USD').toUpperCase() !== userCurrency.toUpperCase() && !ratesLoading && (
                              <span className="text-xs text-gray-500">
                                ≈ {new Intl.NumberFormat('en', { style: 'currency', currency: service.currency || 'USD' }).format(service.price)} {service.currency || 'USD'}
                              </span>
                            )}
                          </div>
                      </div>

                      {displayDesc && <CollapsibleDescription html={displayDesc} />}
                      
                      {service.therapy_tags && service.therapy_tags.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <div className="flex flex-wrap gap-1.5">
                            {service.therapy_tags
                              .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                              .map((tag) => (
                                <Badge 
                                  key={tag.id} 
                                  variant={tag.is_primary ? "default" : "secondary"}
                                  className={`text-xs ${tag.is_primary ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
                                >
                                  {tag.is_primary && <Star className="h-3 w-3 mr-1 fill-current" />}
                                  {i18n.language === 'zh' ? tag.therapy_modality.name_cn : tag.therapy_modality.name_en}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}

                      {(service.adults_only || service.parental_consent_required) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {service.adults_only && (
                            <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
                              <ShieldAlert className="h-3 w-3 mr-1" />
                              18+ Only
                            </Badge>
                          )}
                          {service.parental_consent_required && (
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Parental Consent Required
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {service.formats_available?.includes('virtual') && (
                        <Badge variant="outline">{t('virtual')}</Badge>
                      )}
                      {service.formats_available?.includes('in_person') && (
                        <Badge variant="outline">{t('inPerson')}</Badge>
                      )}
                    </div>
                    <Button 
                      onMouseEnter={() => prefetch(user ? '/booking-datetime' : '/client-auth')}
                      onFocus={() => prefetch(user ? '/booking-datetime' : '/client-auth')}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookNow(service);
                      }}
                      disabled={pendingBookingServiceId === service.id}
                      size="sm"
                      className="ml-3"
                    >
                      {pendingBookingServiceId === service.id ? t('loading', { defaultValue: 'Loading...' }) : ((service.service_track === 'clinical' || (isLicensedMode && !service.service_track)) ? t('requestBooking') : t('bookNow'))}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Role Access Alert Dialog */}
      <AlertDialog open={showRoleAlert} onOpenChange={setShowRoleAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountTypeRestriction')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('practitionerAdminCantBook')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowRoleAlert(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGoToClientLogin}>{t('goToClientLogin')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clinical Cross-State Warning Dialog */}
      <AlertDialog open={showClinicalStateWarning} onOpenChange={(open) => {
        if (!open) {
          setShowClinicalStateWarning(false);
          setPendingClinicalService(null);
          setClinicalWarningAccepted(false);
        }
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {t('clinicalStateWarning.title')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>{t('clinicalStateWarning.body')}</p>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800">
                  <p className="font-medium mb-1">{t('clinicalStateWarning.detectedMismatch')}</p>
                  <p>{t('clinicalStateWarning.yourLocation')}: <span className="font-medium">{userState}</span></p>
                  <p>{t('clinicalStateWarning.practitionerJurisdiction')}: <span className="font-medium">{practitionerJurisdiction}</span></p>
                </div>
                <p className="text-xs text-muted-foreground">{t('clinicalStateWarning.disclaimer')}</p>
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox
                    id="clinical-warning-accept"
                    checked={clinicalWarningAccepted}
                    onCheckedChange={(checked) => setClinicalWarningAccepted(checked === true)}
                  />
                  <Label htmlFor="clinical-warning-accept" className="text-sm leading-tight cursor-pointer">
                    {t('clinicalStateWarning.acceptCheckbox')}
                  </Label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('clinicalStateWarning.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!clinicalWarningAccepted}
              onClick={handleClinicalWarningConfirm}
            >
              {t('clinicalStateWarning.continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PractitionerServices;
